'use client'

import * as React from 'react'
import { getCookie, setCookie } from 'utils/dom'
import invariant from 'tiny-invariant'

import {
  initAmplitude,
  setAmplitudeTrackingAllowed,
  setAmplitudeUser,
  getAmplitudeUser,
  getAmplitudeSessionId,
} from './utils'
import type { AmplitudeAttributionOptions } from './utils'
import type { AmplitudeTrackingState } from './tracking-state'
import { useIsLoggedInQuery } from '../hooks/use-is-logged-in-query'
import { useUserIdQuery } from '../hooks/use-user-id'
import { AMPLITUDE_DEVICE_ID_COOKIE_NAME, AMPLITUDE_SESSION_ID_COOKIE_NAME } from './constants'

// eslint-disable-next-line no-barrel-files/no-barrel-files
// oxlint-disable-next-line cf-web/no-barrel-file
export { AMPLITUDE_DEVICE_ID_COOKIE_NAME, AMPLITUDE_SESSION_ID_COOKIE_NAME }

import { getCookieDomain } from 'utils/env'

const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN
invariant(cookieDomain, 'Cookie domain is not defined inside analytics/amplitude')

const syncAmplitudeSessionCookie = (previousSessionId?: string) => {
  const sessionId = getAmplitudeSessionId()
  if (!sessionId || String(sessionId) === previousSessionId) {
    return
  }
  setCookie({
    name: AMPLITUDE_SESSION_ID_COOKIE_NAME,
    value: String(sessionId),
    expiresIn: 30 * 60 * 1000,
    domain: getCookieDomain(cookieDomain),
  })
}

function syncAmplitudeUser(isLoggedIn: boolean, userId?: string | number) {
  if (!isLoggedIn || !userId) {
    setAmplitudeUser(null)
    return
  }
  const user = `user-${userId}`
  if (getAmplitudeUser() !== user) {
    setAmplitudeUser(user)
  }
}

interface AmplitudeProps {
  enabled?: AmplitudeTrackingState
  /** Turns on the SDK's campaign attribution and sends these first-touch properties once. */
  attribution?: AmplitudeAttributionOptions
}

export const Amplitude = React.memo(({ enabled, attribution }: AmplitudeProps) => {
  const canTrack = enabled === undefined || enabled === true
  const isLoggedInQuery = useIsLoggedInQuery()
  const userIdQuery = useUserIdQuery()
  const amplitudeCookieDeviceId = getCookie(AMPLITUDE_DEVICE_ID_COOKIE_NAME)
  const amplitudeCookieSessionId = getCookie(AMPLITUDE_SESSION_ID_COOKIE_NAME)

  React.useEffect(() => {
    if (enabled !== undefined) {
      setAmplitudeTrackingAllowed(enabled)
    }

    if (!canTrack) {
      return
    }

    let cancelled = false
    void (async () => {
      await initAmplitude(amplitudeCookieDeviceId, amplitudeCookieSessionId, attribution)
      if (cancelled) {
        return
      }
      syncAmplitudeSessionCookie(amplitudeCookieSessionId)
    })()
    return () => {
      cancelled = true
    }
  }, [amplitudeCookieDeviceId, amplitudeCookieSessionId, enabled, canTrack, attribution])

  React.useEffect(() => {
    if (!canTrack || isLoggedInQuery.isFetching || userIdQuery.isFetching) {
      return
    }

    syncAmplitudeUser(Boolean(isLoggedInQuery.data), userIdQuery.data)
  }, [
    canTrack,
    isLoggedInQuery.isFetching,
    isLoggedInQuery.data,
    userIdQuery.isFetching,
    userIdQuery.data,
  ])

  return null
})

Amplitude.displayName = 'Amplitude'
